import React, { useEffect, useMemo, useState } from "react";
import { AdminSidebar } from "./AdminDashboardPage";
import AdminHeader from "./AdminHeader";
import api from "../../services/api";
import { getAllOrders } from "../../services/orderService";
import formatPrice from "../../utils/formatPrice";
import "../../css/admincustomerpage.css";
// Table.jsx lives at src/components/Table.jsx
import { Table } from "../../components/Table";
import { useToast } from "../../contexts/ToastContext";
import { FileDown, RefreshCw, Search, X } from "lucide-react";

// Soft status pill tones (bg / text) from the admin palette.
const STATUS_TONES = {
  Placed: { bg: '#eef1f6', fg: '#4a6382' },
  Processing: { bg: '#fbf1e1', fg: '#9a5b06' },
  Shipped: { bg: '#f3eef6', fg: '#6f5482' },
  'Out for Delivery': { bg: '#e8f3f5', fg: '#2f6f7b' },
  Delivered: { bg: '#eaf3ee', fg: '#2e6a4f' },
  Cancelled: { bg: '#fcefed', fg: '#b42318' },
  Rejected: { bg: '#fcefed', fg: '#b42318' },
  Returned: { bg: '#f3eee9', fg: '#6b5d55' }
};
const DEFAULT_TONE = { bg: '#f3eee9', fg: '#7a6b62' };

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

// Picks the default address if one is marked, otherwise the first
// address on file. Most customers in this app only ever have one.
const getPrimaryAddress = (customer) => {
  if (!customer.addresses?.length) return null;
  return customer.addresses.find((a) => a.isDefault) || customer.addresses[0];
};

// Fixed-width, single-line-per-part cell style with ellipsis truncation.
// Explicitly re-declares nowrap + overflow + ellipsis on a fixed width so
// long text always truncates cleanly inside its own cell instead of
// bleeding into neighboring columns (white-space is inherited, and the
// base table cell class sets nowrap). Full text is on the title tooltip.
const ADDRESS_COL_WIDTH = 190;
const addressLineStyle = {
  width: ADDRESS_COL_WIDTH,
  maxWidth: ADDRESS_COL_WIDTH,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const AdminCustomer = () => {
  const { addToast, confirm } = useToast();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Order-count-per-customer, built from the full order list so the table
  // can show "3 orders" etc. without a separate request per row.
  const [ordersByUserId, setOrdersByUserId] = useState({});

  // Drawer state for the "View Orders" button — fetched fresh (scoped by
  // that customer's email) rather than reused from the bulk list above,
  // so it reflects the latest data and also picks up any guest-checkout
  // orders placed under the same email.
  const [orderDrawerCustomer, setOrderDrawerCustomer] = useState(null);
  const [drawerOrders, setDrawerOrders] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const loadCustomers = async () => {
    try {
      setLoading(true);

      const { data } = await api.get("/admin/users");

      const onlyCustomers = data.filter((user) => !user.isAdmin);

      setCustomers(onlyCustomers);
      setFilteredCustomers(onlyCustomers);
    } catch (err) {
      console.error(err);
      addToast("Failed to load customers", "error");
    } finally {
      setLoading(false);
    }
  };

  // Loads every order once and buckets counts/totals by registered
  // customer (order.user._id). Guest-checkout orders (user: null) aren't
  // tied to any account, so they intentionally don't count toward any
  // customer row here — they're still reachable per-customer via the
  // "View Orders" drawer's email-based search, which also matches
  // shippingAddress.fullName for guest orders.
  const loadOrderCounts = async () => {
    try {
      const allOrders = await getAllOrders({});
      const counts = {};
      allOrders.forEach((order) => {
        const uid = order.user?._id;
        if (!uid) return;
        if (!counts[uid]) counts[uid] = { count: 0, total: 0 };
        counts[uid].count += 1;
        counts[uid].total += order.totalPrice || 0;
      });
      setOrdersByUserId(counts);
    } catch (err) {
      console.error('Failed to load order counts:', err);
    }
  };

  useEffect(() => {
    loadCustomers();
    loadOrderCounts();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();

    const value = search.toLowerCase();

    const filtered = customers.filter((customer) => {
      const addr = getPrimaryAddress(customer);
      return (
        customer.name?.toLowerCase().includes(value) ||
        customer.email?.toLowerCase().includes(value) ||
        customer.phone?.toLowerCase().includes(value) ||
        addr?.pincode?.toLowerCase().includes(value) ||
        addr?.city?.toLowerCase().includes(value) ||
        addr?.state?.toLowerCase().includes(value) ||
        addr?.address?.toLowerCase().includes(value)
      );
    });

    setFilteredCustomers(filtered);
  };

  const handleDelete = async (customer) => {
    const ok = await confirm({
      title: `Delete ${customer.name}?`,
      message: 'This customer account will be removed.',
      confirmLabel: 'Delete'
    });
    if (!ok) return;

    try {
      await api.delete(`/admin/users/${customer._id}`);
      loadCustomers();
    } catch (err) {
      console.error(err);
      addToast("Delete failed", "error");
    }
  };

  const openOrderDrawer = async (customer) => {
    setOrderDrawerCustomer(customer);
    setDrawerLoading(true);
    try {
      // Scoped by email — matches both this customer's own account orders
      // and any guest-checkout orders placed under the same email, thanks
      // to buildOrderFilter's customer matching on the backend.
      const orders = await getAllOrders({ search: customer.email });
      setDrawerOrders(orders);
    } catch (err) {
      console.error('Failed to load customer orders:', err);
      setDrawerOrders([]);
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeOrderDrawer = () => {
    setOrderDrawerCustomer(null);
    setDrawerOrders([]);
  };

  // Downloads the "Customer Report" PDF, scoped to whatever's currently
  // in the search box — same pattern as the products/orders PDF exports.
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await api.get('/admin/users/download-pdf', {
        params: search ? { search } : {},
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customer-report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download customer PDF:', err);
      addToast('Failed to download PDF', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Table.jsx's Cell only receives the row's data, not its index, so the
  // serial number is baked into the row objects up front instead.
  const rows = useMemo(
    () => filteredCustomers.map((c, i) => ({ ...c, _serial: i + 1 })),
    [filteredCustomers]
  );

  const columns = useMemo(
    () => [
      { Header: '#', accessor: '_serial', disableSortBy: true },
      { Header: 'Name', accessor: 'name' },
      {
        Header: 'Email',
        id: 'email',
        // phone-login accounts carry a placeholder like 98xxxx@mithralaya-otp.local
        accessor: (c) => (/\.local$/i.test(c.email || '') ? '— (phone login)' : c.email)
      },
      {
        Header: 'Phone',
        id: 'phone',
        accessor: (c) => c.phone || c.mobile || getPrimaryAddress(c)?.phone || '-'
      },
      {
        Header: 'Date of Birth',
        id: 'dob',
        accessor: (c) => c.dob || c.dateOfBirth || null,
        Cell: ({ row }) => {
          const raw = row.original.dob || row.original.dateOfBirth;
          return raw ? formatDate(raw) : '-';
        }
      },
      {
        Header: 'Address',
        id: 'address',
        disableSortBy: true,
        Cell: ({ row }) => {
          const addr = getPrimaryAddress(row.original);
          if (!addr) return <span className="acp-muted">No address on file</span>;

          const cityState = [addr.city, addr.state].filter(Boolean).join(', ');
          const fullAddress = [addr.address, cityState, addr.pincode].filter(Boolean).join(', ');

          return (
            <div className="acp-address" title={fullAddress}>
              <div style={addressLineStyle}>{addr.address}</div>
              <div className="acp-address-sub" style={addressLineStyle}>{cityState}</div>
            </div>
          );
        }
      },
      {
        Header: 'Pincode',
        id: 'pincode',
        accessor: (c) => getPrimaryAddress(c)?.pincode || '-'
      },
      {
        Header: 'Orders',
        id: 'orders',
        disableSortBy: true,
        accessor: (c) => ordersByUserId[c._id]?.count || 0,
        Cell: ({ row }) => {
          const stats = ordersByUserId[row.original._id];
          return (
            <button
              type="button"
              onClick={() => openOrderDrawer(row.original)}
              title="View this customer's order history"
              className="acp-orders-btn"
            >
              {stats ? `${stats.count} order${stats.count === 1 ? '' : 's'}` : '0 orders'}
              {stats?.total ? ` · ${formatPrice(stats.total)}` : ''}
            </button>
          );
        }
      },
      {
        Header: 'Joined',
        id: 'joined',
        accessor: (c) => (c.createdAt ? new Date(c.createdAt).getTime() : 0),
        Cell: ({ row }) =>
          row.original.createdAt
            ? new Date(row.original.createdAt).toLocaleDateString()
            : '-'
      },
      {
        Header: 'Action',
        id: 'action',
        disableSortBy: true,
        Cell: ({ row }) => (
          <button className="acp-btn-danger" onClick={() => handleDelete(row.original)}>
            Delete
          </button>
        )
      }
    ],
    [ordersByUserId]
  );

  return (
    <div className="admin-layout">
      <AdminSidebar />

      <div className="admin-content">
        <AdminHeader title="Customers" />

        <div className="acp-page">
          <div className="acp-header">
            <div className="acp-header-text">
              <h1>Customers</h1>
              <p className="acp-subtitle">
                Registered shopper accounts, their addresses and order history.
              </p>
            </div>
            <div className="acp-header-actions">
              <button
                type="button"
                className="acp-btn acp-btn-secondary"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                title={search ? `Exports only customers matching "${search}"` : 'Exports all customers'}
              >
                <FileDown size={15} />
                {downloadingPdf ? 'Preparing...' : 'Download PDF'}
              </button>
              <button
                type="button"
                className="acp-btn acp-btn-secondary acp-refresh-btn"
                onClick={() => { loadCustomers(); loadOrderCounts(); }}
              >
                <RefreshCw size={15} />
                Refresh
              </button>
            </div>
          </div>

          <form className="acp-search-form" onSubmit={handleSearch}>
            <div className="acp-search-field">
              <Search size={15} className="acp-search-icon" />
              <input
                className="acp-search-input"
                type="text"
                placeholder="Search name, email, phone, address, pincode..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <button className="acp-btn acp-btn-primary acp-search-btn" type="submit">
              Search
            </button>
          </form>

          {loading ? (
            <div className="acp-loading">Loading customers...</div>
          ) : (
            <Table columns={columns} data={rows} show />
          )}
        </div>
      </div>

      {orderDrawerCustomer && (
        <div className="acp-drawer-overlay" onClick={closeOrderDrawer}>
          <aside className="acp-drawer" onClick={(e) => e.stopPropagation()}>
            <header className="acp-drawer-head">
              <div className="acp-drawer-who">
                <h2>{orderDrawerCustomer.name}</h2>
                <p>{orderDrawerCustomer.email}</p>
              </div>
              <button
                type="button"
                className="acp-drawer-close"
                onClick={closeOrderDrawer}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </header>

            <div className="acp-drawer-body">
              {drawerLoading ? (
                <p className="acp-drawer-empty">Loading orders...</p>
              ) : drawerOrders.length === 0 ? (
                <p className="acp-drawer-empty">No orders found for this customer.</p>
              ) : (
                <div className="acp-drawer-list">
                  {drawerOrders.map((order) => {
                    const tone = STATUS_TONES[order.status] || DEFAULT_TONE;
                    return (
                      <div key={order._id} className="acp-drawer-order">
                        <div className="acp-drawer-order-id">
                          <strong>{order.orderId}</strong>
                          <span>{formatDate(order.createdAt)}</span>
                        </div>
                        <span
                          className="acp-status-pill"
                          style={{ background: tone.bg, color: tone.fg }}
                        >
                          {order.status}
                        </span>
                        <span className="acp-drawer-order-total">
                          {formatPrice(order.totalPrice)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default AdminCustomer;
